const SCENEACCESS_HOST = 'sceneaccess.io';
const SCENEACCESS_WWW_HOST = `www.${SCENEACCESS_HOST}`;
const CANONICAL_PRODUCTION_ORIGIN = `https://${SCENEACCESS_HOST}`;
const THAI_LOCALE = 'th';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const stripTrailingSlashes = value =>
  String(value || '')
    .trim()
    .replace(/\/+$/, '');

// Create slug from random texts.
// From Gist thread: https://gist.github.com/mathewbyrne/1280286
const createSlug = str => {
  let text = String(str || '')
    .toLowerCase()
    .trim();

  const sets = [
    { to: 'a', from: 'ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶ' },
    { to: 'c', from: 'ÇĆĈČ' },
    { to: 'd', from: 'ÐĎĐÞ' },
    { to: 'e', from: 'ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ' },
    { to: 'g', from: 'ĜĞĢǴ' },
    { to: 'h', from: 'ĤḦ' },
    { to: 'i', from: 'ÌÍÎÏĨĪĮİỈỊ' },
    { to: 'j', from: 'Ĵ' },
    { to: 'ij', from: 'Ĳ' },
    { to: 'k', from: 'Ķ' },
    { to: 'l', from: 'ĹĻĽŁ' },
    { to: 'm', from: 'Ḿ' },
    { to: 'n', from: 'ÑŃŅŇ' },
    { to: 'o', from: 'ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ' },
    { to: 'oe', from: 'Œ' },
    { to: 'p', from: 'ṕ' },
    { to: 'r', from: 'ŔŖŘ' },
    { to: 's', from: 'ßŚŜŞŠ' },
    { to: 't', from: 'ŢŤ' },
    { to: 'u', from: 'ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ' },
    { to: 'w', from: 'ẂŴẀẄ' },
    { to: 'x', from: 'ẍ' },
    { to: 'y', from: 'ÝŶŸỲỴỶỸ' },
    { to: 'z', from: 'ŹŻŽ' },
    { to: '-', from: "·/_,:;'" },
  ];

  sets.forEach(set => {
    text = text.replace(new RegExp(`[${set.from}]`, 'gi'), set.to);
  });

  const slug = encodeURIComponent(
    text
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '')
      .replace(/--+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '')
  );

  return slug.length > 0 ? slug : 'no-slug';
};

const isSceneAccessHost = hostname => {
  const host = String(hostname || '').toLowerCase();
  return host === SCENEACCESS_HOST || host === SCENEACCESS_WWW_HOST;
};

const normalizeCanonicalOrigin = value => {
  const raw = stripTrailingSlashes(value);
  if (!raw) {
    return CANONICAL_PRODUCTION_ORIGIN;
  }

  try {
    const url = new URL(raw.includes('://') ? raw : `https://${raw}`);
    if (isSceneAccessHost(url.hostname)) {
      return CANONICAL_PRODUCTION_ORIGIN;
    }

    const port = url.port ? `:${url.port}` : '';
    return `${url.protocol}//${url.hostname}${port}`;
  } catch (e) {
    return raw;
  }
};

const extractUuid = id => {
  if (!id) {
    return null;
  }
  if (typeof id === 'string') {
    return id;
  }
  if (typeof id === 'object' && typeof id.uuid === 'string') {
    return id.uuid;
  }
  return null;
};

const isUuid = value => UUID_RE.test(String(value || ''));

const encodePathSegment = value => encodeURIComponent(String(value || ''));

const getListingTitle = listing => listing?.attributes?.title || listing?.title || '';

const normalizeSeoLocale = locale =>
  String(locale || '')
    .toLowerCase()
    .startsWith(THAI_LOCALE)
    ? THAI_LOCALE
    : 'en';

const localeFromPath = pathname =>
  /^\/th(?:\/|$)/i.test(String(pathname || '')) ? THAI_LOCALE : 'en';

const stripLocalePrefix = path => {
  const value = String(path || '') || '/';
  const match = value.match(/^([^?#]*)([?#].*)?$/);
  const pathname = (match?.[1] || '/').replace(/^\/th(?=\/|$)/i, '') || '/';
  return `${pathname.startsWith('/') ? pathname : `/${pathname}`}${match?.[2] || ''}`;
};

const localizePath = (path, locale) => {
  const value = stripLocalePrefix(path);
  const match = value.match(/^([^?#]*)([?#].*)?$/);
  const pathname = match?.[1] || '/';
  const suffix = match?.[2] || '';

  if (normalizeSeoLocale(locale) !== THAI_LOCALE) {
    return `${pathname}${suffix}`;
  }

  return `${pathname === '/' ? '/th' : `/th${pathname}`}${suffix}`;
};

const hasThaiListingTranslation = listing => {
  const thai = listing?.attributes?.publicData?.thLanguage?.th;
  return Boolean(
    (typeof thai?.title === 'string' && thai.title.trim()) ||
      (typeof thai?.description === 'string' && thai.description.trim())
  );
};

const canonicalListingPath = params => {
  const id = extractUuid(params?.id || params?.listingId);
  if (!id) {
    return null;
  }

  const slug = createSlug(params?.slug || params?.title || '');
  return localizePath(`/l/${slug}/${encodePathSegment(id)}`, params?.locale);
};

const canonicalListingPathFromListing = (listing, locale) =>
  canonicalListingPath({
    id: listing?.id,
    title: getListingTitle(listing),
    locale,
  });

const canonicalListingUrl = (origin, paramsOrListing, locale) => {
  const root = normalizeCanonicalOrigin(origin);
  const path =
    paramsOrListing?.attributes || paramsOrListing?.id?.uuid
      ? canonicalListingPathFromListing(paramsOrListing, locale)
      : canonicalListingPath({ ...paramsOrListing, locale: locale || paramsOrListing?.locale });
  return path ? `${root}${path}` : null;
};

const blogSlugFromPageId = pageId => {
  const raw = String(pageId || '')
    .replace(/\.json$/, '')
    .split('/')
    .pop();
  const withoutPrefix = raw.startsWith('blog-') ? raw.slice('blog-'.length) : raw;
  return withoutPrefix.replace(/-thai$/i, '');
};

const canonicalBlogPath = params => {
  const rawValue = params?.slug || params?.pageId;
  const rawSlug = blogSlugFromPageId(rawValue);
  if (!rawSlug) {
    return null;
  }
  const inferredLocale = /-thai(?:\.json)?$/i.test(String(rawValue || '')) ? THAI_LOCALE : 'en';
  return localizePath(`/blog/${createSlug(rawSlug)}`, params?.locale || inferredLocale);
};

const canonicalBlogUrl = (origin, params) => {
  const path = canonicalBlogPath(params);
  return path ? `${normalizeCanonicalOrigin(origin)}${path}` : null;
};

module.exports = {
  CANONICAL_PRODUCTION_ORIGIN,
  SCENEACCESS_HOST,
  SCENEACCESS_WWW_HOST,
  createSlug,
  isSceneAccessHost,
  normalizeCanonicalOrigin,
  normalizeSeoLocale,
  localeFromPath,
  stripLocalePrefix,
  localizePath,
  hasThaiListingTranslation,
  extractUuid,
  isUuid,
  canonicalListingPath,
  canonicalListingPathFromListing,
  canonicalListingUrl,
  blogSlugFromPageId,
  canonicalBlogPath,
  canonicalBlogUrl,
};
